import type { SceneNode } from "../scene/model";
import { effects, hasEffects } from "../native/effects";
import { isInteractive } from "../native/interactive";
import { vectorPath } from "../native/vector-path";
import { bounds } from "../native/geometry";
import { multiply, nodeMatrix } from "../native/transforms";
import type { AffineMatrix } from "../geometry/affine";
import { renderCanvas, type RenderCanvas } from "./canvas-surface";
import { paintShape } from "./native-shape-path";
import { paintVector } from "./native-vector-path";
import type { PixelBudget } from "./pixel-budget";

/** Paint/alpha are not pixel effects. Simple vectors never need a progressive queue. */
export function directVector(node: SceneNode) {
  if (
    node.layer.clipToBelow ||
    node.geometry.warpCorners ||
    isInteractive(node)
  )
    return false;
  const e = effects(node);
  if (
    e.hue ||
    e.saturation ||
    e.blur ||
    e.shadow ||
    e.background.enabled ||
    e.edge.style !== "none"
  )
    return false;
  return (
    ["rectangle", "ellipse", "polygon"].includes(node.kind) ||
    (node.kind === "path" &&
      !node.geometry.svgPath &&
      vectorPath(node).anchors.length <= 512)
  );
}

export function vectorTile(node: SceneNode) {
  return directVector(node) && (node.kind === "path" || hasEffects(node));
}

type Tile = {
  canvas: RenderCanvas;
  box: ReturnType<typeof bounds>;
  density: number;
};

/** GPU adapter for ordinary vector paint. Local pixels are shared by equal copies,
 * retained by the common LRU across viewport changes, and replaced synchronously.
 * Object pose/opacity never enter the tile; source geometry remains editable. */
export class NativeVectorDisplay {
  private tiles = new Map<string, Tile>();
  private keys = new WeakMap<SceneNode, string>();
  private allowance = 16_000_000;
  constructor(private pixels: PixelBudget) {}
  private key(node: SceneNode) {
    let key = this.keys.get(node);
    if (!key) {
      const { vector, ...geometry } = node.geometry;
      const {
        opacity: _opacity,
        visible: _visible,
        blendMode: _blend,
        ...appearance
      } = node.appearance;
      void _opacity;
      void _visible;
      void _blend;
      const path = vector ? vectorPath(node) : undefined;
      key = JSON.stringify([
        node.kind,
        geometry,
        path && {
          anchors: path.anchors,
          closed: path.closed,
          arrow: path.arrow,
        },
        appearance,
      ]);
      this.keys.set(node, key);
    }
    return key;
  }
  begin(nodes: SceneNode[]) {
    const unique = new Set(nodes.filter(vectorTile).map((n) => this.key(n)));
    this.allowance = Math.min(
      16_000_000,
      (this.pixels.limit * 0.45) / (4 * Math.max(1, unique.size)),
    );
  }
  get(node: SceneNode, density: number) {
    const key = this.key(node),
      m = nodeMatrix(node),
      w = node.geometry.width!,
      h = node.geometry.height!,
      pose: AffineMatrix = [m[0] / w, m[1] / w, m[2] / h, m[3] / h, m[4], m[5]];
    const stretch = Math.max(1, Math.hypot(pose[0], pose[1], pose[2], pose[3]));
    const local: SceneNode = {
      ...node,
      transform: { x: 0, y: 0 },
      appearance: { ...node.appearance, opacity: 1 },
    };
    const previous = this.tiles.get(key);
    const b = previous?.box ?? bounds(local);
    const box = previous?.box ?? {
      x: b.x - 2,
      y: b.y - 2,
      width: b.width + 4,
      height: b.height + 4,
    };
    const requested =
      2 ** Math.ceil(Math.log2(Math.max(0.125, density * stretch)));
    const scale = Math.min(
      requested,
      8192 / box.width,
      8192 / box.height,
      Math.sqrt(this.allowance / (box.width * box.height)),
    );
    let entry = previous;
    if (
      !entry ||
      entry.density < scale * 0.999 ||
      entry.canvas.width * entry.canvas.height > this.allowance * 1.1
    ) {
      const canvas = renderCanvas();
      canvas.width = Math.max(1, Math.ceil(box.width * scale));
      canvas.height = Math.max(1, Math.ceil(box.height * scale));
      const ctx = canvas.getContext("2d")!;
      ctx.setTransform(
        canvas.width / box.width,
        0,
        0,
        canvas.height / box.height,
        (-box.x * canvas.width) / box.width,
        (-box.y * canvas.height) / box.height,
      );
      if (node.kind === "path") paintVector(ctx, local);
      else paintShape(ctx, local);
      if (entry) this.remove(key, entry);
      entry = { canvas, box, density: scale };
      this.tiles.set(key, entry);
      const held = entry;
      this.pixels.add(
        canvas,
        canvas.width * canvas.height * 4,
        "vectorTiles",
        () => this.remove(key, held),
      );
    }
    this.pixels.touch(entry.canvas);
    return {
      canvas: entry.canvas,
      matrix: multiply(pose, [
        box.width,
        0,
        0,
        box.height,
        box.x + box.width / 2,
        box.y + box.height / 2,
      ]),
    };
  }
  private remove(key: string, entry: Tile) {
    this.pixels.remove(entry.canvas);
    entry.canvas.width = entry.canvas.height = 0;
    if (this.tiles.get(key) === entry) this.tiles.delete(key);
  }
  dispose() {
    for (const [key, tile] of this.tiles) this.remove(key, tile);
  }
}
